import { FLAG_ENABLE_CH_DASHBOARD_ANALYTICS } from '@app/features/analytics/constants';
import { useFlags } from '@app/hooks/use-flags';
import { useTranslation } from '@hopin-team/provider-translation';
import { Button } from '@hopin-team/ui-button';
import cx from 'classnames';
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { DateTimePickerProps } from 'react-flatpickr';
import { useForm } from 'react-hook-form';

import {
  getDateRangeTrackingTab,
  getTimeRangeTrackingValue,
} from '../../analytics-tracking';
import { InlineFlatpickr } from '../../components/inline-flatpickr/inline-flatpickr';
import { useAnalytics } from '../../hooks/use-analytics';
import { AngleRight } from '../../icons/angle-right';
import { CalendarIcon } from '../../icons/calendar';
import { CalendarActiveIcon } from '../../icons/calendar-active';
import { CheckIcon } from '../../icons/check';
import { CollapsedIcon } from '../../icons/collapsed';
import { ExpandedIcon } from '../../icons/expanded';
import { useAnalyticsContext } from '../../providers/analytics-provider';
import { InterHeaderStyles, OutlineOnFocus } from '../../styles';
import { formatDateForFilter } from '../../utils';

type Option = {
  label: string;
  value: string;
  submenu?: boolean;
  hide?: boolean;
};

type FormValues = {
  [key: string]: string;
};

export const DateFilter = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [submenuOpen, setSubmenuOpen] = useState(false);

  const [focusedIndex, setFocusedIndex] = useState(0);
  const flags = useFlags();
  const { t } = useTranslation();
  const { state, dispatch } = useAnalyticsContext();
  const dateFilter = state.dateRange;
  const [selected, setSelected] = useState<string>(dateFilter);
  const { track } = useAnalytics();
  const enableCHDashboardAnalytics =
    flags?.[FLAG_ENABLE_CH_DASHBOARD_ANALYTICS];

  const flatpickrOptions = useMemo<DateTimePickerProps['options']>(
    () => ({
      altFormat: 'm/d/Y',
      altInput: true,
      allowInput: true,
      dateFormat: 'Y-m-d',
      enableTime: false,
      maxDate: 'today',
    }),
    [],
  );

  const menuRef = useRef<HTMLDivElement>(null);

  const {
    formState: { errors, isValid },
    reset,
    control,
    watch,
  } = useForm<FormValues>({
    defaultValues: {
      startDate: state.startDate
        ? new Date(state.startDate).toISOString().split('T')[0]
        : '',
      endDate: state.endDate
        ? new Date(state.endDate).toISOString().split('T')[0]
        : '',
    },
    mode: 'onChange',
  });

  const resetDate = useCallback(() => {
    reset({
      startDate: '',
      endDate: '',
    });
  }, [reset]);

  const setStateDate = useCallback(() => {
    reset({
      startDate: state.startDate
        ? new Date(state.startDate).toISOString().split('T')[0]
        : '',
      endDate: state.endDate
        ? new Date(state.endDate).toISOString().split('T')[0]
        : '',
    });
  }, [reset, state.startDate, state.endDate]);

  const onChange = (
    dateRange: string,
    startDate?: string,
    endDate?: string,
  ) => {
    setSelected(dateRange);
    dispatch({
      type: `update date filter`,
      payload: { dateRange, startDate, endDate },
    });
    track('ola_date_range_dropdown_click', {
      range: dateRange,
      startDate,
      endDate,
      time_range: getTimeRangeTrackingValue(dateRange),
      tab: getDateRangeTrackingTab({
        selectedTab: state.selectedTab,
        enableCHDashboardAnalytics,
      }),
    });
    setIsOpen(false);
    setSubmenuOpen(false);
    if (dateRange !== 'custom') {
      resetDate();
    }
  };

  const handleCustomDate = () => {
    const startDate = formatDateForFilter(new Date(watch('startDate')));
    const endDate = formatDateForFilter(new Date(watch('endDate')));
    setIsOpen(false);
    setSubmenuOpen(false);
    onChange(`custom`, startDate, endDate);
  };

  const options: Option[] = [
    { label: t('analytics.last-30-days'), value: 'last-30-days' },
    { label: t('analytics.last-3-months'), value: 'last-3-months' },
    { label: t('analytics.last-6-months'), value: 'last-6-months' },
    { label: t('analytics.last-year'), value: 'last-year' },
    {
      label: t('analytics.all-time'),
      value: 'all-time',
    },
    {
      label: t('analytics.custom'),
      value: 'custom',
      submenu: true,
    },
  ];

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      const target = event.target as Node;
      const flatpickrElements = document.querySelectorAll(
        '.flatpickr-calendar',
      );

      const isClickInsideFlatpickr = Array.from(flatpickrElements).some(
        element => element.contains(target),
      );

      if (
        menuRef.current &&
        !menuRef.current.contains(target) &&
        !isClickInsideFlatpickr
      ) {
        setIsOpen(false);
        setSubmenuOpen(false);
        if (dateFilter === 'custom') {
          setStateDate();
        } else {
          resetDate();
        }
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [dateFilter, reset, resetDate, setStateDate, state]);

  const handleKeyDown = (event: React.KeyboardEvent<HTMLButtonElement>) => {
    if (!isOpen && (event.key === 'Enter' || event.key === ' ')) {
      event.preventDefault();
      setIsOpen(true);
      return;
    }

    if (!isOpen) return;

    if (event.key === 'ArrowDown') {
      event.preventDefault(); // Prevents browser scroll
      setFocusedIndex(prev => (prev + 1) % options.length);
    } else if (event.key === 'ArrowUp') {
      event.preventDefault(); // Prevents browser scroll
      setFocusedIndex(prev => (prev - 1 + options.length) % options.length);
    } else if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      const selectedOption = options[focusedIndex];
      if (selectedOption.submenu) {
        setSubmenuOpen(true);
      } else {
        onChange(selectedOption.value);
      }
    } else if (event.key === 'Escape') {
      event.preventDefault();
      setIsOpen(false);
      setSubmenuOpen(false);
    }
  };

  return (
    <div className="relative w-40" ref={menuRef}>
      {/* Trigger Button */}
      <button
        className={cx(
          'relative w-40 h-10 py-1 px-10 text-left border border-neutral-b4 rounded-lg shadow-sm',
          {
            'ring-2 ring-blue-500': isOpen,
          },
          InterHeaderStyles,
          OutlineOnFocus,
        )}
        onClick={() => setIsOpen(!isOpen)}
        onKeyDown={handleKeyDown}
      >
        <span className="absolute inset-y-0 left-2 flex items-center">
          {isOpen ? <CalendarActiveIcon /> : <CalendarIcon />}
        </span>
        <span
          className={cx(
            'block truncate',
            {
              'text-blue-500': isOpen,
            },
            InterHeaderStyles,
          )}
        >
          {t(`analytics.${dateFilter}`)}
        </span>
        <span className="absolute inset-y-0 right-2 flex items-center pointer-events-none">
          {isOpen ? <ExpandedIcon /> : <CollapsedIcon />}
        </span>
      </button>

      {/* Menu */}
      {isOpen && (
        <div className="absolute w-40 py-1 mt-1 bg-white border border-neutral-b4 rounded-lg shadow-lg outline-none list-none z-10">
          {options
            .filter(option => !option.hide)
            .map((option, index) => (
              <div key={option.value} className="relative">
                <div
                  role="button"
                  tabIndex={0}
                  className={`select-none px-3 py-2 cursor-pointer hover:bg-blue-100 ${
                    index === focusedIndex ? 'bg-blue-100' : ''
                  } ${selected === option.value ? 'bg-blue-100' : ''}`}
                  onClick={() => {
                    if (option.submenu) {
                      setSubmenuOpen(true);
                    } else {
                      onChange(option.value);
                      setIsOpen(false);
                    }
                  }}
                  onKeyDown={e => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      if (option.submenu) {
                        setSubmenuOpen(true);
                      } else {
                        onChange(option.value);
                        setIsOpen(false);
                      }
                    }
                  }}
                  onMouseEnter={() => setFocusedIndex(index)}
                >
                  <>
                    <span
                      className={cx(
                        'truncate flex items-center justify-between',
                        {
                          'text-blue-500': selected === option.value,
                        },
                        InterHeaderStyles,
                      )}
                    >
                      {option.label}
                      {option.submenu && (
                        <span className="ml-2">
                          <AngleRight />
                        </span>
                      )}
                    </span>
                    {selected === option.value && option.value !== 'custom' ? (
                      <span className="absolute inset-y-0 right-5 flex items-center">
                        <CheckIcon />
                      </span>
                    ) : null}
                  </>
                </div>

                {/* Custom Date Picker Submenu */}
                {submenuOpen && option.value === 'custom' && (
                  <div className="absolute right-full top-0 mr-2 bg-white border rounded-lg shadow-lg p-4 w-80">
                    <div className="mb-3">
                      <span className="font-inter descriptor-mini mb-1">
                        {t('analytics.start-date') + ' *'}
                      </span>
                      <InlineFlatpickr
                        id="startDate"
                        name="startDate"
                        placeholder={t('analytics.start-date-placeholder')}
                        value={watch('startDate')}
                        control={control}
                        options={flatpickrOptions}
                      />
                      {(errors.endDate?.message ||
                        (watch('startDate') &&
                          watch('endDate') &&
                          new Date(watch('startDate')) >
                            new Date(watch('endDate')))) && (
                        <div
                          id={`endDate-error`}
                          className="mt-2 flex text-xs text-red-500"
                          data-testid="inline-save-field-error"
                          aria-live="polite"
                        >
                          {t('analytics.date-comparison-error')}
                        </div>
                      )}
                    </div>

                    <div className="mb-3">
                      <span className="font-inter descriptor-mini mb-1">
                        {t('analytics.end-date') + ' *'}
                      </span>
                      <InlineFlatpickr
                        id="endDate"
                        name="endDate"
                        placeholder={t('analytics.end-date-placeholder')}
                        value={watch('endDate')}
                        control={control}
                        options={flatpickrOptions}
                      />
                    </div>

                    <div className="flex gap-4">
                      <Button
                        onClick={() => {
                          reset({
                            startDate: '',
                            endDate: '',
                          });
                        }}
                        pattern="secondary"
                        size="small"
                        isOutlined
                        className="flex-1 px-4 py-2 border border-neutral-b4 text-subtitle !rounded-lg"
                      >
                        {t('analytics.clear')}
                      </Button>
                      <Button
                        onClick={handleCustomDate}
                        disabled={
                          !isValid ||
                          !!(
                            watch('startDate') &&
                            watch('endDate') &&
                            new Date(watch('startDate')) >
                              new Date(watch('endDate'))
                          )
                        }
                        pattern="primary"
                        size="small"
                        className="flex-1 px-4 py-2 bg-blue-400 text-white !rounded-lg text-subtitle"
                      >
                        {t('analytics.apply')}
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            ))}
        </div>
      )}
    </div>
  );
};
